import { useRef, useMemo, useState } from "react";
import { Marker } from "react-leaflet";
import L from "leaflet";
import { RecordScreenCard } from "@/lib/Components/Screens/RecordScreen/Cards/RecordScreenCard";
import { Map } from "@/components/ui/map";
import { useGqlMutation, useGqlQuery } from "@/lib/GraphQLCodegen/fetcher";
import { updateCityMutation } from "@/app/Cities/Cities/GraphQL/cityMutations";
import { cityRecordQuery } from "@/app/Cities/Cities/GraphQL/cityRecordQuery";
import { toast } from "sonner";
import { Button } from "@/components/catalyst/button";
import { BasemapLayer } from "@/components/map/BasemapLayer";

type CityMapCardProps = {
  cityId: string;
  lat: number;
  lng: number;
};

const cityIcon = L.divIcon({
  html: `<div style="background-color: #3b82f6; width: 30px; height: 30px; border-radius: 50%; display: flex; align-items: center; justify-content: center; border: 3px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.3);"></div>`,
  className: "",
  iconSize: [30, 30],
  iconAnchor: [15, 15],
});

export function CityMapCard({ cityId, lat, lng }: CityMapCardProps) {
  const markerRef = useRef<L.Marker>(null);
  const { refetch } = useGqlQuery(cityRecordQuery, { id: cityId });
  const { mutateAsync: updateCity, isPending } =
    useGqlMutation(updateCityMutation);

  const [draggedPosition, setDraggedPosition] = useState<
    [number, number] | null
  >(null);

  const position = useMemo(() => [lat, lng] as [number, number], [lat, lng]);

  const displayPosition = draggedPosition ?? position;
  const hasChanges = draggedPosition !== null;

  const handleDragEnd = () => {
    const marker = markerRef.current;
    if (!marker) return;

    const { lat: newLat, lng: newLng } = marker.getLatLng();
    setDraggedPosition([newLat, newLng]);
  };

  const handleSave = async () => {
    if (!draggedPosition) return;

    try {
      await updateCity({
        id: cityId,
        input: { lat: draggedPosition[0], lng: draggedPosition[1] },
      });
      setDraggedPosition(null);
      toast.success("City coordinates updated");
      refetch();
    } catch (error) {
      toast.error("Failed to update coordinates");
      console.error(error);
    }
  };

  const handleCancel = () => {
    setDraggedPosition(null);
  };

  return (
    <RecordScreenCard
      title="Location"
      subtitle={`${displayPosition[0].toFixed(5)}, ${displayPosition[1].toFixed(5)}`}
      buttons={
        hasChanges ? (
          <div className="flex gap-2">
            <Button onClick={handleCancel} outline>
              Cancel
            </Button>
            <Button onClick={handleSave} color="green" disabled={isPending}>
              {isPending ? "Saving..." : "Save"}
            </Button>
          </div>
        ) : null
      }
    >
      <Map center={position} zoom={10} scrollWheelZoom={false} height="300px">
        <BasemapLayer />
        <Marker
          ref={markerRef}
          position={displayPosition}
          icon={cityIcon}
          draggable
          eventHandlers={{ dragend: handleDragEnd }}
        />
      </Map>
    </RecordScreenCard>
  );
}
